import { getIconClassName } from '@uifabric/styling/lib/index';
import { observer as observerLite } from 'mobx-react-lite';
import { IconButton } from '@fluentui/react/lib/Button';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon } from '@fluentui/react/lib/Icon';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { AttachmentOpenAction, getAttachmentDefaultOpenAction } from 'owa-attachment-data';
import { AttachmentFile } from 'owa-attachment-file-types';
import { AttachmentDragData } from 'owa-attachment-drag-and-drop';
import createAttachmentFileFromExistingMailboxAttachment from 'owa-attachment-file-types/lib/utils/createAttachmentFileFromExistingAttachment';
import { ActionMessageId, AttachmentFullViewState } from 'owa-attachment-full-data';
import setAttachmentShouldShowContextMenu from 'owa-attachment-full-data/lib/actions/setAttachmentShouldShowContextMenu';
import {
    AttachmentSurfaceActionButton,
    datapoints,
    lazyGetContextMenus,
    SurfaceAction,
} from 'owa-attachment-full-view';
import getAttachmentInfoSecondLineInfo from 'owa-attachment-full-view/lib/utils/getAttachmentInfoSecondLineInfo';
import shouldShowProgressIndicator from 'owa-attachment-full-view/lib/utils/shouldShowProgressIndicator';
import {
    AttachmentModel,
    lazyShouldShowImageView,
    TypeOfAttachment,
} from 'owa-attachment-model-store';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import AttachmentClass from 'owa-attachment-model-store/lib/store/schema/AttachmentClass';
import { lazyClickAttachment } from 'owa-attachment-select-actions';
import { AttachmentIconView, AttachmentThumbnailView } from 'owa-attachment-view';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import Draggable from 'owa-dnd/lib/components/Draggable';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { isThumbnailableDocument } from 'owa-file';
import { FileIcon, getIconForFile } from 'owa-file-icon';
import loc from 'owa-localize';
import { MoreActionsButton } from 'owa-locstrings/lib/strings/moreactionsbutton.locstring.json';
import { InstrumentationContext } from 'owa-mail-store';
import { formatRelativeDate } from 'owa-observable-datetime';
import {
    AttachmentPreviewMode,
    PreviewPaneSelectionSource,
    SXSID_MAIN_WINDOW,
} from 'owa-sxs-store';
import { ImageGalleryItem } from 'owa-sxsdata';
import { trace } from 'owa-trace';
import * as React from 'react';

export interface ConversationAttachmentWellItemProps {
    attachmentViewState: AttachmentFullViewState;
    parentItemId: ClientItemId;
    itemReceivedTime: string;
    instrumentationContext: InstrumentationContext;
    onBeforePreview?: () => void;
    onFocus?: (attachmentId: string) => void;
    createImageGallery: () => Promise<ImageGalleryItem[]>;
}

const styles = require('./ConversationAttachmentWellItem.scss');
const classNames = require('classnames/bind').bind(styles);

const DRAG_X_OFFSET = -12;
const DRAG_Y_OFFSET = 5;

export default observerLite(function ConversationAttachmentWellItem(
    props: ConversationAttachmentWellItemProps
) {
    const containerElement = React.useRef<HTMLDivElement>();
    const contextMenuJustOpened = React.useRef<boolean>();

    React.useEffect(
        () => {
            // scroll if the menu items are out of view after the context menu was just opened
            if (contextMenuJustOpened.current) {
                contextMenuJustOpened.current = false;
                if (props.attachmentViewState.shouldShowContextMenu) {
                    const wellHeight = containerElement.current.parentElement.clientHeight;
                    const wellScroll = containerElement.current.parentElement.scrollTop;
                    const itemHeight = containerElement.current.offsetHeight;
                    const itemOffset = containerElement.current.offsetTop;
                    if (itemHeight + itemOffset > wellHeight + wellScroll) {
                        containerElement.current.parentElement.scrollTop =
                            itemHeight + itemOffset - wellHeight;
                    } else if (itemOffset < wellScroll) {
                        containerElement.current.parentElement.scrollTop = itemOffset;
                    }
                }
            }
        }
        // No dependency array, to ensure the effect is run on each render
    );

    const renderIcon = (attachment: AttachmentModel): JSX.Element => {
        const shouldShowImageView = lazyShouldShowImageView.tryImportForRender();
        if (shouldShowImageView?.(attachment.model)) {
            const attachmentName: string = attachment.model.Name;
            const fileIcon: FileIcon = getIconForFile(attachmentName);
            let fileIconClass: string = null;
            let placeholderCssClass: string = getIconClassName(ControlIcons.Photo2);
            if (isThumbnailableDocument(attachmentName)) {
                fileIconClass = fileIcon.small;
                placeholderCssClass = fileIcon.small;
            }
            return (
                <div className={styles.thumbnailDiv}>
                    <AttachmentThumbnailView
                        attachmentViewState={props.attachmentViewState}
                        placeHolderCSSClass={classNames(placeholderCssClass, styles.placeholder)}
                        fileIconCssClass={fileIconClass}
                        imageLoadErrorCSSClass={fileIcon.small}
                        thumbnailWidth={32}
                        thumbnailHeight={32}
                    />
                </div>
            );
        }
        return <AttachmentIconView attachmentId={props.attachmentViewState.attachmentId} />;
    };
    const renderInfo = (attachment: AttachmentModel): JSX.Element => {
        const isClassicWithNoActionMessage: boolean =
            attachment.type !== TypeOfAttachment.Reference &&
            props.attachmentViewState.actionMessage === ActionMessageId.None;
        const secondLineMessageInfo = isClassicWithNoActionMessage
            ? {
                  text: formatRelativeDate(props.itemReceivedTime),
                  title: null,
                  shouldDisplaySuccessIcon: false,
                  shouldDisplayFailureIcon: false,
              }
            : getAttachmentInfoSecondLineInfo(
                  props.attachmentViewState,
                  attachment,
                  false /* useSaveToCloudLink */
              );
        return (
            <div className={styles.infoDiv}>
                <div
                    className={classNames(styles.firstLine, styles.textOverFlow)}
                    title={attachment.model.Name}>
                    {attachment.model.Name}
                </div>
                <div
                    className={classNames(styles.secondLine, styles.textOverFlow)}
                    title={secondLineMessageInfo.title}>
                    {secondLineMessageInfo.shouldDisplaySuccessIcon ? (
                        <Icon
                            iconName={ControlIcons.Accept}
                            className={classNames(styles.icon, styles.success)}
                        />
                    ) : null}
                    {secondLineMessageInfo.shouldDisplayFailureIcon ? (
                        <Icon
                            iconName={ControlIcons.Cancel}
                            className={classNames(styles.icon, styles.failure)}
                        />
                    ) : null}
                    {secondLineMessageInfo.text}
                </div>
            </div>
        );
    };
    const renderContextMenu = (attachment: AttachmentModel): JSX.Element => {
        const getContextMenus = lazyGetContextMenus.tryImportForRender();
        const contextMenuItems: IContextualMenuItem[] = getContextMenus
            ? getContextMenus(
                  props.attachmentViewState,
                  attachment,
                  {
                      viewState: props.attachmentViewState,
                      previewPaneSelectionSource:
                          PreviewPaneSelectionSource.ConversationAttachmentWell,
                      parentItemId: props.parentItemId,
                      isDraft: false,
                      isEdit: false,
                      onBeforePreview: props.onBeforePreview,
                      createImageGallery: props.createImageGallery,
                      instrumentationContext: props.instrumentationContext,
                      sxsId: null, // pass if SxS is supported and can be supported with multiple views
                      targetWindow: window,
                  },
                  props.parentItemId,
                  null,
                  containerElement.current.parentElement,
                  null /* moveAttachmentToInlineHandler */
              )
            : [];
        return (
            <>
                {contextMenuItems.map(menuItem => {
                    return (
                        <ContextMenuItem
                            key={menuItem.key}
                            attachmentViewState={props.attachmentViewState}
                            menuItem={menuItem}
                        />
                    );
                })}
            </>
        );
    };
    const renderProgressIndicator = (): JSX.Element => {
        if (shouldShowProgressIndicator(props.attachmentViewState)) {
            return (
                <div className={styles.progressIndicator}>
                    <ProgressIndicator
                        percentComplete={props.attachmentViewState.actionCompletePercent}
                    />
                </div>
            );
        }
        return null;
    };
    const keyDown = (event: React.KeyboardEvent<unknown>) => {
        if (event.keyCode === KeyCodes.enter || event.keyCode === KeyCodes.space) {
            onClick();
        }
    };
    const onClick = (ev?: any) => {
        const defaultOpenAction = getAttachmentDefaultOpenAction(
            props.attachmentViewState.strategy
        );
        if (defaultOpenAction !== AttachmentOpenAction.NoAction) {
            const { name: eventName, customData: customData } = datapoints.OnClick;
            logUsage(eventName, customData(defaultOpenAction));
            lazyClickAttachment(
                props.attachmentViewState,
                PreviewPaneSelectionSource.ConversationAttachmentWell,
                props.instrumentationContext,
                props.parentItemId,
                false,
                props.onBeforePreview,
                props.createImageGallery,
                containerElement.current.parentElement,
                window
            );
        }
    };
    const onContextMenu = (evt: React.MouseEvent<unknown>) => {
        evt.preventDefault();
        setAttachmentShouldShowContextMenu(
            props.attachmentViewState,
            (contextMenuJustOpened.current = true)
        );
    };
    const onMenuButtonClicked = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        setAttachmentShouldShowContextMenu(
            props.attachmentViewState,
            (contextMenuJustOpened.current = !props.attachmentViewState.shouldShowContextMenu)
        );
    };
    const onFocusOrMouseOver = () => {
        props.onFocus?.(props.attachmentViewState.attachmentId.Id);
    };
    const canDrag = (): boolean => {
        return !!getAttachment(props.attachmentViewState.attachmentId);
    };
    const getAttachmentDragData = (): AttachmentDragData => {
        const attachment = getAttachment(props.attachmentViewState.attachmentId).model;
        const attachmentFile: AttachmentFile = createAttachmentFileFromExistingMailboxAttachment(
            attachment
        );
        const attachmentDragData: AttachmentDragData = {
            itemType: DraggableItemTypes.Attachment,
            attachmentFile: attachmentFile,
        };
        return attachmentDragData;
    };
    const getDragPreview = (): HTMLElement => {
        const elem = document.createElement('div');
        elem.className = styles.dragPreview;
        elem.innerText = getAttachment(props.attachmentViewState.attachmentId).model.Name;
        return elem;
    };
    const attachment = getAttachment(props.attachmentViewState.attachmentId);
    const isForAddToCalendar =
        !!attachment && attachment.attachmentClass === AttachmentClass.CalendarEvent;
    return (
        <div
            aria-label={attachment.model.Name}
            ref={containerElement}
            onFocus={onFocusOrMouseOver}
            onKeyDown={keyDown}
            onMouseOver={onFocusOrMouseOver}
            className={classNames(styles.container, {
                active: props.attachmentViewState.shouldShowContextMenu,
            })}
            tabIndex={0}
            data-is-focusable={true}
            onClick={onClick}
            onContextMenu={onContextMenu}>
            <Draggable
                canDrag={canDrag}
                getDragData={getAttachmentDragData}
                getDragPreview={getDragPreview}
                xOffset={DRAG_X_OFFSET}
                yOffset={DRAG_Y_OFFSET}>
                <div className={styles.innerContainer}>
                    {renderIcon(attachment)}
                    {renderInfo(attachment)}
                    {props.attachmentViewState.strategy.showActionsMenu &&
                        props.attachmentViewState.shouldShowActionsContainer && (
                            <div className={styles.actionsContainer}>
                                <AttachmentSurfaceActionButton
                                    attachmentViewState={props.attachmentViewState}
                                    isForImageView={false}
                                    previewState={AttachmentPreviewMode.None}
                                    shouldNotHandleOnBlur={true}
                                    action={
                                        isForAddToCalendar
                                            ? SurfaceAction.addToCalendar
                                            : SurfaceAction.download
                                    }
                                    parentItemId={props.parentItemId}
                                    sxsId={SXSID_MAIN_WINDOW}
                                />
                                <IconButton
                                    data-is-focusable={false}
                                    className={styles.menuButton}
                                    onClick={onMenuButtonClicked}
                                    title={loc(MoreActionsButton)}
                                    ariaLabel={loc(MoreActionsButton)}
                                    onKeyDown={keyDownForMenuButton}
                                    /* note: onClick will be triggered automatically as well */ iconProps={{
                                        iconName: props.attachmentViewState.shouldShowContextMenu
                                            ? 'ChevronUp'
                                            : 'ChevronDown',
                                        styles: {
                                            root: styles.icon,
                                        },
                                    }}
                                />
                            </div>
                        )}
                </div>
            </Draggable>
            {renderProgressIndicator()}
            {props.attachmentViewState.shouldShowContextMenu ? renderContextMenu(attachment) : null}
        </div>
    );
});

interface ContextMenuItemProps {
    attachmentViewState: AttachmentFullViewState;
    menuItem: IContextualMenuItem;
}

const ContextMenuItem = observerLite(function ContextMenuItem(props: ContextMenuItemProps) {
    const onClickCallback = React.useCallback(
        (evt: React.MouseEvent<HTMLElement>) => {
            evt.stopPropagation();
            props.menuItem.onClick(evt, props.menuItem);
            setAttachmentShouldShowContextMenu(props.attachmentViewState, false);
        },
        [props.menuItem, props.attachmentViewState]
    );

    const onKeyDownCallback = React.useCallback(
        (evt: React.KeyboardEvent<unknown>) => {
            if (evt.keyCode === KeyCodes.enter || evt.keyCode === KeyCodes.space) {
                evt.stopPropagation();
                props.menuItem.onClick();
                setAttachmentShouldShowContextMenu(props.attachmentViewState, false);
            }
        },
        [props.menuItem, props.attachmentViewState]
    );

    return (
        <div
            tabIndex={0}
            data-is-focusable={true}
            className={styles.menuItem}
            onClick={onClickCallback}
            onKeyDown={onKeyDownCallback}>
            {props.menuItem.name}
        </div>
    );
});

function keyDownForMenuButton(event: React.KeyboardEvent<unknown>) {
    /* internal behavior of IconButton: if onKeyDown event is triggered, onClick event will be triggered automatically as well */
    if (event.keyCode === KeyCodes.enter || event.keyCode === KeyCodes.space) {
        trace.info('AttachmentMenuButton.keyDown enter|space');
        event.stopPropagation();
    }
}
